import { HttpClient } from '@angular/common/http';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { environment } from '@ion/env';
import { JWT } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { map, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { PermissionCheckOptions } from './auth.model';
import { AuthenticationService } from './authentication.service';

/**
 * Attribute name in the JWT
 */
type JwtAttributeNames = 'custom:rkc_permissions' | 'custom:rkc_subtenant_id';

const selectedSubtenantEndpoint = `${environment.apiUrl}/subtenant/selected`;
export type SelectedSubtenantResponse = { subtenantId?: string };

/**
 * The AuthorizationService is responsible for providing the permissions state of the user.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthorizationService implements OnDestroy {
  private featureFlags = () => environment.featureFlags;
  private readonly http = inject(HttpClient);

  /**
   * Global feature flag to toggle all authorization flags in the application.
   */
  private featureFlagAuthorizationChecksEnabled = () => this.featureFlags().authorizationChecks;

  private authenticationService = inject(AuthenticationService);

  private readonly permissionsSubject = new ReplaySubject<string[]>(1);
  private readonly permissions$: Observable<string[]> = this.permissionsSubject.asObservable();
  private readonly subtenantSubject = new ReplaySubject<string | null>(1);
  private subtenantInitialized = false;
  private removeHubListener?: () => void;

  constructor() {
    if (environment.enableAuthentication) {
      this.refreshPermissions();
      this.removeHubListener = Hub.listen('auth', ({ payload }) => this.onAuthEvent(payload?.event));
    } else {
      this.permissionsSubject.next([]);
      this.subtenantSubject.next(null);
      this.subtenantInitialized = true;
    }
  }

  ngOnDestroy(): void {
    this.removeHubListener?.();
    this.permissionsSubject.complete();
    this.subtenantSubject.complete();
  }

  /**
   * Checks if the current user has any of the given permissions.
   */
  userHasAnyPermission(
    permissions: string | string[],
    permissionCheckOptions?: PermissionCheckOptions,
  ): Observable<boolean> {
    return this.checkPermission('any', permissions, permissionCheckOptions);
  }

  /**
   * Checks if the current user has all of the given permissions.
   */
  userHasAllPermissions(
    permissions: string | string[],
    permissionCheckOptions?: PermissionCheckOptions,
  ): Observable<boolean> {
    return this.checkPermission('all', permissions, permissionCheckOptions);
  }

  private checkPermission(
    type: 'any' | 'all',
    checkPermissions: string | string[],
    permissionCheckOptions?: PermissionCheckOptions,
  ): Observable<boolean> {
    if (!environment.enableAuthentication) {
      // when we don't use Cognito (only locally), we assume that the user has the permission
      return of(true);
    }
    if (
      !this.featureFlagAuthorizationChecksEnabled() ||
      (permissionCheckOptions?.checkIfFeatureFlagEnabled != null &&
        this.featureFlags()[permissionCheckOptions.checkIfFeatureFlagEnabled] === false)
    ) {
      // feature flag disabled (for the feature or globally), so we emulate that the user has the permission
      return of(true);
    }

    const checkPermissionsArr = typeof checkPermissions === 'string' ? [checkPermissions] : checkPermissions;

    if (environment.permissionMode === 'DEVELOPMENT') {
      // turn this on when you want to disable / enable permissions locally while developing

      const manuallyDefinedPermissions = environment.grantedPermissionsForDevelopment;
      const hasPermission =
        type === 'any'
          ? checkPermissionsArr.some(checkPermission => manuallyDefinedPermissions.includes(checkPermission))
          : checkPermissionsArr.every(checkPermission => manuallyDefinedPermissions.includes(checkPermission));

      return of(hasPermission);
    }

    // and now really check permissions against user data
    return this.permissions$.pipe(
      map(userPermissions => {
        return type === 'any'
          ? checkPermissionsArr.some(checkPermission => userPermissions.includes(checkPermission))
          : checkPermissionsArr.every(checkPermission => userPermissions.includes(checkPermission));
      }),
    );
  }

  refreshPermissions(): void {
    this.authenticationService
      .getIdToken()
      .then(jwt => this.permissionsSubject.next(this.stringJwtAttributes(jwt, 'custom:rkc_permissions')))
      .catch(() => this.permissionsSubject.next([]));
  }

  private refreshSubtenant(): void {
    this.subtenantInitialized = true;
    this.http
      .get<{ subtenantId?: string }>(selectedSubtenantEndpoint, { withCredentials: true })
      .pipe(
        map(res => res.subtenantId?.trim() || null),
        catchError(() => of(null)),
      )
      .subscribe(subtenant => this.subtenantSubject.next(subtenant));
  }

  private onAuthEvent(event: string | undefined) {
    if (!event) {
      return;
    }

    if (event === 'signOut') {
      this.permissionsSubject.next([]);
      this.subtenantSubject.next(null);
      this.subtenantInitialized = false;
      return;
    }

    if (event === 'signIn' || event === 'tokenRefresh' || event === 'cognitoHostedUI' || event === 'customOAuthState') {
      this.refreshPermissions();
    }
  }

  selectedSubtenant$(): Observable<string | null> {
    if (!this.subtenantInitialized) {
      this.refreshSubtenant();
    }
    return this.subtenantSubject.asObservable();
  }

  isFleetOperator$(): Observable<boolean> {
    return this.selectedSubtenant$().pipe(
      map(subtenant => !!subtenant),
      catchError(() => of(false)),
    );
  }

  selectSubtenant(subtenantId: string): Observable<SelectedSubtenantResponse> {
    return this.http
      .post(selectedSubtenantEndpoint, null, {
        withCredentials: true,
        params: { value: subtenantId },
      })
      .pipe(tap(() => this.subtenantSubject.next(subtenantId)));
  }

  deselectSubtenant(): Observable<SelectedSubtenantResponse> {
    return this.http
      .delete(selectedSubtenantEndpoint, {
        withCredentials: true,
      })
      .pipe(tap(() => this.subtenantSubject.next(null)));
  }

  getJwtSubtenants(): Promise<string[]> {
    return this.authenticationService
      .getIdToken()
      .then(jwt => this.stringJwtAttributes(jwt, 'custom:rkc_subtenant_id'));
  }

  private stringJwtAttributes(jwt: JWT | null, attributeName: JwtAttributeNames): string[] {
    if (!jwt) return [];

    const attrValue = jwt.payload[attributeName];
    if (typeof attrValue !== 'string') {
      return [];
    }

    return attrValue
      .split(';')
      .map((s: string) => s.trim())
      .filter(Boolean);
  }
}
